import Container from "@/components/commons/Container";
import SectionHeader from "@/components/tactsuit/common/SectionHeader";
import { useTrans } from "@/utils/useTrans";
import { Product } from "@/api/product-api";
import DisplayProducts from "@/components/tactsuit/components/DisplayProducts";
import { SizeLineCardCta } from "@/components/tactsuit/components/size-line-card-cta";
import { getProductDetailHref } from "@/libs/product/detail-href";
import { cn } from "@/libs/utils";
import React, { useState } from "react";
import ProductBanner from "@/components/tactsuit/main/ProductBanner";
import { getServerSideProductProps } from "@/libs/getServerSideProductProps";
import PageTemplate from "@/components/templates/PageTemplate";
import { ProductUtil } from "@/utils/ProductUtil";
import ProductSelector from "@/components/tactsuit/components/ProductSelector";
import TactSuitCard from "@/v2-components/TactSuitCard";
import Link from "next/link";
import { usePricedFilter } from "@/hooks/usePricedFilter";
import { useSetFirstBases } from "@/components/tactsuit/components/buy-tactsuit/set-deal";

const Section = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => <div className={cn("mt-16 desktop:mt-24", className)}>{children}</div>;

type Props = {
  product: Product;
  products: Product[];
};

export default function Accessories({ product, products }: Props) {
  const { t, tp } = useTrans(["tactsuit"]);
  const desc = t("You can purchase accessories needed for bHaptics products.");
  const priced = usePricedFilter();
  const listed = priced(products);
  // 수량 세트가 성립하면 세트를 대표 카드로(#6008) — 판정은 PDP 기본 선택·카트 묶기와 같은 술어.
  const setFirstBases = useSetFirstBases(listed);
  const accessories = ProductUtil.categorizeTactSuitAccessories(listed, {
    setFirstBases,
  });

  return (
    <PageTemplate>
      <PageTemplate.Content>
        {/* <ProductBanner
          product={{
            ...product,
            bannerImage: {
              desktopSrc: "/images/tactsuit/accessories/banner.jpg",
              mobileSrc: "/images/tactsuit/accessories/m-banner.jpg",
            },
            name: "Accessories",
            desc: desc,
            prices: [],
          }}
        /> */}
        <Container className={"px-4 py-12 desktop:py-16"}>
          <SectionHeader className={"text-center laptop:text-left"}>
            {t("TactSuit Accessories")}
          </SectionHeader>
          <Categorized className="mt-5" categorized={accessories} />
          {accessories.glove.length > 0 && (
            <Section>
              <SectionHeader
                className={"text-center laptop:py-6 laptop:text-left"}
              >
                {t("Glove Accessories")}
              </SectionHeader>
              <DisplayProducts
                className="mt-5"
                products={accessories.glove.map(tp)}
              />
            </Section>
          )}
        </Container>
      </PageTemplate.Content>
    </PageTemplate>
  );
}

const KEYMAP = {
  All: "allWithoutGlove",
  "For Pro": "pro",
  "For Air": "air",
} as const;

const CATEGORIES: CategoryLabel[] = ["All", "For Pro", "For Air"];

type CategoryLabel = keyof typeof KEYMAP;
type CategorizedProducts = ReturnType<
  typeof ProductUtil.categorizeTactSuitAccessories
>;

const Categorized = ({
  categorized,
  className,
}: {
  categorized: CategorizedProducts;
  className?: string;
}) => {
  const { t } = useTrans(["tactsuit"]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const current = CATEGORIES[currentIndex];
  const currentCategoryProducts = categorized[KEYMAP[current]];

  return (
    <div className={cn(className)}>
      <ProductSelector
        current={t(current)}
        categories={CATEGORIES.map((c) => t(c))}
        onChange={(translatedCategory) => {
          // Find the index by comparing translations
          const index = CATEGORIES.findIndex(
            (c) => t(c) === translatedCategory,
          );
          if (index !== -1) {
            setCurrentIndex(index);
          }
        }}
      />
      <div
        className={cn(
          "mt-4 grid grid-cols-1 gap-4 md:grid-cols-3 laptop:mt-8 laptop:gap-6 laptop:px-0 desktop:grid-cols-4",
          "grid-cols-2 gap-6",
        )}
      >
        {(currentCategoryProducts ?? []).map((p, i) => {
          return <AccessoryCard key={i} product={p} />;
        })}
      </div>
    </div>
  );
};

const AccessoryCard = ({ product }: { product: Product }) => {
  const { t } = useTrans(["tactsuit"]);
  const isSoldOut = ProductUtil.isProductSoldOut(product);
  return (
    <TactSuitCard product={product}>
      <div className="group flex w-full flex-col items-center justify-center">
        <div className="min-h-5">
          <TactSuitCard.Tags />
        </div>
        <div
          className={cn(
            "relative mt-3 aspect-square size-[156px] cursor-pointer laptop:size-[224px]",
            isSoldOut && "pointer-events-none",
          )}
        >
          <Link href={getProductDetailHref(product.slug)}>
            <TactSuitCard.Thumbnail
              fill
              className="laptop:hover-scale object-cover"
            />
          </Link>
        </div>
        <TactSuitCard.Name className="mt-5 text-center text-base font-500 laptop:mt-6" />
        <TactSuitCard.Description
          description={t(product.shortDesc)}
          className="mt-3 text-center text-xs"
        />
        <TactSuitCard.PriceDetail className="mt-3 text-center text-base [&_p]:!font-400" />
        <div className="mt-6 laptop:mt-7">
          {/* 접힌 사이즈 라인 카드는 담지 않고 PDP 로 — 대표 사이즈가 그대로 담겼다(#5922 R1). */}
          <SizeLineCardCta product={product}>
            <TactSuitCard.NotifyOrBuy
              label={t("Add to Bag")}
              allowBundleSuggestion={false}
              showSkuOptionsDialog={true}
              variant={"blue-fill"}
            />
          </SizeLineCardCta>
        </div>
      </div>
    </TactSuitCard>
  );
};

export const getServerSideProps = getServerSideProductProps(null);
